import {Helmet} from 'react-helmet'
import {useTranslation, TKey} from '@/translations/i18n.tsx'
import {
    AuthFormCard,
    AuthFormCardSubtitle,
    AuthFormCardTitle,
    AuthPageContainer
} from '@features/user/components/auth-form-atoms'
import {InputText} from '@components/commons/input-text'
import {Button} from '@components/ui/button'
import {ButtonLink} from '@components/commons/button-link'
import {routes} from '@utilities/constants/routes.tsx'
import {z} from 'zod'
import {useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {useAsync} from '@hooks/useAsync.ts'
import {httpLogin} from '@features/user/http/user.http.ts'
import {errorHandler} from '@utilities/helpers.ts'
import {Spinner} from '@components/ui/spinner'
import {parseAxiosPromise} from '@/axiosInstance.ts'
import {captureException} from '@sentry/react'
import {useAuthStore} from '@features/user/store/store.ts'
import {PhpUser, phpUserToUserPartial} from '@features/user/types.ts'

const LoginValidationSchema = z.object({
    email: z.string().min(1, {message: 'errors:required'}).email({message: 'errors:email_invalid'}),
    password: z.string().min(1, {message: 'errors:required'}).min(8, {message: 'errors:password_invalid_length'})
})
type LoginValidationSchema = z.infer<typeof LoginValidationSchema>

export const Login = () => {
    const {t} = useTranslation()
    const setAuthTokens = useAuthStore(store => store.setTokens)
    const setUser = useAuthStore(store => store.setUser)
    const login = useAsync()
    const form = useForm<LoginValidationSchema>({resolver: zodResolver(LoginValidationSchema)})

    const handleSubmit = form.handleSubmit(async formValues => {
        try {
            const response = await parseAxiosPromise({
                axiosPromise: login.run(httpLogin({payload: formValues})),
                onZodError: captureException,
                responseShape: z.object({
                    access_token: z.string().min(1),
                    refresh_token: z.string().min(1),
                    user: PhpUser.partial()
                })
            })

            setAuthTokens({
                accessToken: response.access_token,
                refreshToken: response.refresh_token
            })
            setUser(phpUserToUserPartial(response.user))
        } catch (error) {
            errorHandler(error)
        }
    })

    return (
        <AuthPageContainer>
            <Helmet title={t('titles:login')} />
            <AuthFormCard onSubmit={handleSubmit}>
                <div className="flex flex-col items-center gap-6">
                    <AuthFormCardTitle>{t('auth:login')}</AuthFormCardTitle>
                    <AuthFormCardSubtitle>{t('auth:enter_email_password')}</AuthFormCardSubtitle>
                </div>
                <div className="flex flex-col items-center gap-6">
                    <InputText
                        type="text"
                        className="w-full"
                        label={t('commons:email')}
                        errorMessage={t((form.formState.errors.email?.message ?? '') as TKey)}
                        {...form.register('email')}
                    />
                    <InputText
                        type="password"
                        visibilityToggle
                        className="w-full"
                        label={t('commons:password')}
                        errorMessage={t((form.formState.errors.password?.message ?? '') as TKey)}
                        {...form.register('password')}
                    />
                </div>
                <div className="flex flex-col items-center gap-6">
                    <Button size="lg" disabled={login.isLoading} className="w-full" type="submit">
                        {t('auth:login')}
                        {login.isLoading && <Spinner />}
                    </Button>
                    <ButtonLink
                        size="xs"
                        to={routes.FORGOT_PASSWORD.handle.fullPath}
                        variant="tertiary"
                        disabled={login.isLoading}
                    >
                        {t('commons:forgot_password')}
                    </ButtonLink>
                </div>
            </AuthFormCard>
        </AuthPageContainer>
    )
}
